import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useEffectiveAuth } from './GhostContext';
import { supabase } from '../lib/supabase';
import { getCountryConfig } from '../lib/countryConfig';
import { sortClassesForCountry } from '../lib/classOrder';
import { getManagementOnlyClassIds } from '../lib/classGroups';

export interface ClassOption {
  id: string;
  name: string;
  grade_level: number;
  tier: number | null;
  // Nigeria expansion (branch/stream mechanism): grade_group_id links sibling
  // branches of the same grade together, and sort_order gives a stable
  // ordering among them (and among classes generally). Both are real columns
  // on `classes` — they just weren't previously selected or declared here.
  // Pakistan classes have grade_group_id null, same as before this fix.
  grade_group_id: string | null;
  sort_order: number;
}

interface ClassesContextType {
  classes: ClassOption[];
  // Ids of branched grades' management representatives. Greyed out / not
  // selectable in pickers; empty for Pakistan. Recomputed from the live list,
  // so the moment a grade's branches are gone the class is a normal class again.
  managementOnlyIds: Set<string>;
  loading: boolean;
  refreshClasses: () => Promise<void>;
}

// Any screen that adds, renames, deletes, reorders or branches a class calls
// notifyClassesChanged() so every other screen's class list (students
// dropdown, filters, pickers…) reloads — before this, the shared list was only
// ever fetched once on login, so new / renamed classes never showed up until a
// full page reload.
const CLASSES_CHANGED_EVENT = 'septa:classes-changed';
export function notifyClassesChanged() {
  try { window.dispatchEvent(new Event(CLASSES_CHANGED_EVENT)); } catch { /* non-browser */ }
}

const EMPTY_SET: Set<string> = new Set();
const ClassesContext = createContext<ClassesContextType>({
  classes: [], managementOnlyIds: EMPTY_SET, loading: false, refreshClasses: async () => {},
});

export function ClassesProvider({ children }: { children: React.ReactNode }) {
  // Ghost-aware: when an admin is viewing a school as its principal, the
  // class list has to be that school's, not the admin's (who has none).
  const { school } = useEffectiveAuth();
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshClasses = useCallback(async () => {
    if (!school?.id) { setClasses([]); return; }
    setLoading(true);
    let query = supabase
      .from('classes')
      .select('id, name, grade_level, tier, grade_group_id, sort_order')
      .eq('school_id', school.id)
      .eq('is_active', true);
    // Tier → grade_level → sort_order (Nigeria). Pakistan classes are all
    // tier=null, so they keep sort_order-only ordering.
    if (getCountryConfig(school.country).ordersClassesByTier) {
      query = query
        .order('tier', { ascending: true, nullsFirst: false })
        .order('grade_level', { ascending: true });
    }
    const { data } = await query.order('sort_order');
    // Sorted again in memory with the same rule, so the order is right even
    // when the database returns ties in an arbitrary order.
    setClasses(sortClassesForCountry((data || []) as ClassOption[], school.country));
    setLoading(false);
  }, [school?.id, school?.country]);

  useEffect(() => { refreshClasses(); }, [refreshClasses]);

  useEffect(() => {
    const handler = () => { refreshClasses(); };
    window.addEventListener(CLASSES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CLASSES_CHANGED_EVENT, handler);
  }, [refreshClasses]);

  const managementOnlyIds = useMemo(
    () => (getCountryConfig(school?.country).usesBranchGroupTargeting
      ? getManagementOnlyClassIds(classes)
      : EMPTY_SET),
    [classes, school?.country],
  );

  return (
    <ClassesContext.Provider value={{ classes, managementOnlyIds, loading, refreshClasses }}>
      {children}
    </ClassesContext.Provider>
  );
}

export function useClasses() {
  return useContext(ClassesContext);
}

// For screens that fetch their own class list (most of them do): ask the
// shared context which of those ids are management-only, without changing the
// screen's own query.
export function useManagementClassIds(): Set<string> {
  return useContext(ClassesContext).managementOnlyIds;
}

// A screen's selected class must never sit on a management-only class (it has
// no roster of its own). If the selection lands on one — e.g. the first class
// in the list is a branched grade — move it to the first real class.
export function useAvoidManagementClass(
  selectedId: string,
  setSelectedId: (id: string) => void,
  list: { id: string }[],
) {
  const ids = useManagementClassIds();
  useEffect(() => {
    if (!selectedId || !ids.has(selectedId)) return;
    const first = list.find(c => !ids.has(c.id));
    if (first) setSelectedId(first.id);
  }, [ids, list, selectedId, setSelectedId]);
}

// Shared look for a greyed-out management class in a button/pill list.
export const MANAGEMENT_CLASS_HINT = 'Branched — pick one of its branches';
